/**
 * Color palette system
 * Each theme has its own colorPalette.js file with raw color definitions
 * componentColors.json maps component references to colorPalette values
 */

// Import theme color palettes
import lightColorPalette from '../themes/light.colorPalette';
import darkColorPalette from '../themes/dark.colorPalette';
import componentColors from '../themes/componentColors.json';

/**
 * Theme configuration - maps theme names to their color palettes
 */
export const THEMES = {
  light: lightColorPalette,
  dark: darkColorPalette,
};

// Legacy exports for backwards compatibility
export const COLORS = lightColorPalette;
export const DARK_COLORS = darkColorPalette;

/**
 * Current theme state (can be managed by your app state)
 */
let currentTheme = 'light';

/**
 * Set the current theme
 * @param {string} theme - 'light' or 'dark'
 */
export const setTheme = (theme) => {
  if (THEMES[theme]) {
    currentTheme = theme;
  } else {
    // eslint-disable-next-line no-console
    console.warn(`Theme "${theme}" not found. Available themes: ${Object.keys(THEMES).join(', ')}`);
  }
};

/**
 * Get the current theme
 * @returns {string} Current theme name
 */
export const getCurrentTheme = () => currentTheme;

/**
 * Get a color value from the color palette
 * @param {string} colorName - The name of the color (e.g., 'blue', 'primary')
 * @param {number} shade - The shade value (0-100)
 * @param {string} theme - Optional theme override ('light' or 'dark')
 * @returns {string} The hex color value
 */
export const getColor = (colorName, shade = 50, theme = null) => {
  const activeTheme = theme || currentTheme;
  const colorPalette = THEMES[activeTheme];

  if (!colorPalette) {
    // eslint-disable-next-line no-console
    console.warn(`Theme "${activeTheme}" not found`);
    return '#000000';
  }

  if (!colorPalette[colorName]) {
    // eslint-disable-next-line no-console
    console.warn(`Color "${colorName}" not found in ${activeTheme} palette`);
    return '#000000';
  }

  if (!colorPalette[colorName][shade]) {
    // eslint-disable-next-line no-console
    console.warn(`Shade "${shade}" not found for color "${colorName}" in ${activeTheme} theme`);
    return colorPalette[colorName][50] || '#000000';
  }

  return colorPalette[colorName][shade];
};

/**
 * Resolve a component color reference (e.g., "gray.20") to an actual color value
 * @param {string} colorRef - Color reference in format "colorName.shade" (e.g., "gray.20")
 * @param {string} theme - Optional theme override
 * @returns {string} The hex color value
 */
export const resolveColorRef = (colorRef, theme = null) => {
  if (!colorRef || typeof colorRef !== 'string') {
    return '#000000';
  }

  // Handle special cases like "none"
  if (colorRef === 'none') {
    return 'transparent';
  }

  const parts = colorRef.split('.');
  if (parts.length !== 2) {
    // eslint-disable-next-line no-console
    console.warn(
      `Invalid color reference format: "${colorRef}". Expected format: "colorName.shade"`
    );
    return '#000000';
  }

  const [colorName, shadeStr] = parts;
  const shade = parseInt(shadeStr, 10);

  if (Number.isNaN(shade)) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid shade value in color reference: "${colorRef}"`);
    return '#000000';
  }

  return getColor(colorName, shade, theme);
};

/**
 * Get a component color by path (e.g., "background.primary")
 * @param {string} path - Component color path (e.g., "background.primary", "text.secondary")
 * @param {string} theme - Optional theme override
 * @returns {string} The hex color value
 */
export const getComponentColor = (path, theme = null) => {
  const activeTheme = theme || currentTheme;

  // Navigate through the componentColors object using the path
  const pathParts = path.split('.');
  let current = componentColors;

  for (let i = 0; i < pathParts.length; i += 1) {
    if (current[pathParts[i]] === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`Component color path not found: "${path}"`);
      return '#000000';
    }
    current = current[pathParts[i]];
  }

  // If we've reached a string value, it's a color reference (e.g., "gray.20")
  if (typeof current === 'string') {
    return resolveColorRef(current, activeTheme);
  }

  // If it's still an object, return it (for nested structures)
  return current;
};

/**
 * Helper function to get a CSS custom property string
 * @param {string} colorName - The name of the color
 * @param {number} shade - The shade value (0-100)
 * @param {string} theme - Optional theme override ('light' or 'dark')
 * @returns {string} CSS custom property string
 */
export const getCSSVar = (colorName, shade = 50, theme = null) => {
  const activeTheme = theme || currentTheme;
  return `var(--color-${activeTheme}-${colorName}-${shade})`;
};

/**
 * Generate CSS custom properties for all colors
 * @param {string} theme - Optional theme override ('light' or 'dark')
 * @returns {string} CSS string with all color variables
 */
export const generateCSSVariables = (theme = null) => {
  const activeTheme = theme || currentTheme;
  const colorPalette = THEMES[activeTheme];

  if (!colorPalette) {
    // eslint-disable-next-line no-console
    console.warn(`Theme "${activeTheme}" not found`);
    return '';
  }

  let css = ':root {\n';

  Object.entries(colorPalette).forEach(([colorName, shades]) => {
    Object.entries(shades).forEach(([shade, value]) => {
      css += `  --color-${activeTheme}-${colorName}-${shade}: ${value};\n`;
    });
  });

  css += '}';
  return css;
};

/**
 * Generate CSS custom properties for both light and dark themes
 * @returns {string} CSS string with all color variables for both themes
 */
export const generateAllCSSVariables = () => {
  let css = '';

  Object.entries(THEMES).forEach(([themeName, colorPalette]) => {
    css += `/* ${themeName} theme */\n`;
    css += `[data-theme="${themeName}"] {\n`;

    Object.entries(colorPalette).forEach(([colorName, shades]) => {
      Object.entries(shades).forEach(([shade, value]) => {
        css += `  --color-${colorName}-${shade}: ${value};\n`;
      });
    });

    css += '}\n\n';
  });

  return css;
};

/**
 * Toggle between light and dark themes
 * @returns {string} The new theme name
 */
export const toggleTheme = () => {
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  setTheme(newTheme);
  return newTheme;
};

/**
 * Get all available theme names
 * @returns {string[]} Array of theme names
 */
export const getAvailableThemes = () => Object.keys(THEMES);

/**
 * Check if a theme exists
 * @param {string} theme - Theme name to check
 * @returns {boolean} True if theme exists
 */
export const themeExists = (theme) => Object.prototype.hasOwnProperty.call(THEMES, theme);

/**
 * Get a color from a specific theme
 * @param {string} theme - Theme name
 * @param {string} colorName - Color name
 * @param {number} shade - Shade value (0-100)
 * @returns {string} The hex color value
 */
export const getThemeColor = (theme, colorName, shade = 50) => {
  return getColor(colorName, shade, theme);
};
